"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="h-[400px] rounded-lg bg-muted/10 animate-pulse border border-primary/10" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive">Failed to load featured products.</p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground">No featured products found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
      {products.map((product, i) => <div key={product.id} data-index={i} className="group relative flex flex-col bg-card/40 backdrop-blur-md border border-primary/20 rounded-lg overflow-hidden transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] hover:-translate-y-1">
          <Link to={product.link} className="aspect-square max-w-xs mx-auto overflow-hidden relative">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full bg-muted/20 flex items-center justify-center">
                <span className="text-muted-foreground/40 font-mono">NO IMAGE</span>
              </div>}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60" />
          </Link>

          <div className="p-6 flex flex-col flex-grow gap-3">
            <div className="flex justify-between items-start gap-2">
              <Link to={product.link}>
                <h3 className="font-serif font-bold text-lg text-foreground group-hover:text-primary transition-colors line-clamp-1 uppercase tracking-wider">
                  {product.title}
                </h3>
              </Link>
            </div>

            <div className="font-mono text-xl font-bold text-accent-foreground">
              ${product.price.toLocaleString()}
            </div>

            {product.short_description && <div className="text-sm text-muted-foreground line-clamp-2 font-sans leading-relaxed" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} />}

            <div className="mt-auto pt-4 flex flex-col gap-2">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-foreground-primary font-serif uppercase tracking-widest py-6 rounded-sm hover:shadow-[0_0_15px_rgba(59,130,246,0.4)] transition-all" text="Add to Cart" showQuantity={false} />
              <Link to={product.link} className="w-full">
                <Button className="w-full bg-primary text-foreground-primary font-serif uppercase tracking-widest py-6 rounded-sm hover:shadow-[0_0_15px_rgba(59,130,246,0.4)] transition-all">
                  BUY NOW
                </Button>
              </Link>
            </div>
          </div>
        </div>)}
    </div>;
}
export default function TheCalmSanctuary() {
  const sectionReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const wp_query_params = {
    per_page: 4,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="223413" id="featured-gaming-products" className="bg-background text-foreground relative overflow-hidden ![padding:50px_0px_128px_0px] !h-[1050px]">
      <div className="mx-auto w-full max-w-[1600px] px-6 sm:px-8 lg:px-16 relative z-10">
        <div ref={sectionReveal.ref} className={`transition-all duration-1000 ease-out ${sectionReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="flex flex-col gap-4 mb-16">
            <h2 className="font-serif font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-primary uppercase">
              Featured Products
            </h2>
            <div className="h-1 w-24 bg-primary rounded-full" />
            <p className="font-mono text-muted-foreground max-w-2xl">
              Equip yourself with the rarest items and elite services from our curated collection.
            </p>
          </div>

          <EcommerceProductsProvider wp_query={{
          per_page: 4,
          order: "desc",
          orderby: "date"
        }}>
            <ProductsGridConsumer />
          </EcommerceProductsProvider>

          <div className="mt-16 flex justify-center">
            <Link to="/ShopPage">
              <Button variant="outline" className="border-primary/30 hover:border-primary hover:bg-primary/5 text-primary font-serif uppercase tracking-widest px-12 py-6 h-auto">
                View All Items
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute top-1/4 -left-24 w-[500px] h-[500px] bg-primary/5 blur-[100px] rounded-full pointer-events-none -z-10" />
      <div className="absolute bottom-1/4 -right-24 w-[600px] h-[600px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none -z-10" />
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642964/img/carbon-fibre.png')] opacity-[0.03] pointer-events-none" />
    </section>;
}